/**
 * Modèles 3D : glb optimisés de public/assets/models (pipeline tools/assets), avec repli procédural
 * si un fichier manque. Convention : nez vers −Z, taille réelle, centrés (plan §4).
 */
import { assetUrl } from '../assets';
import {
  Color3,
  Mesh,
  MeshBuilder,
  PBRMaterial,
  StandardMaterial,
  TransformNode,
  LoadAssetContainerAsync,
  type AssetContainer,
  type Scene,
} from '@babylonjs/core';
import '@babylonjs/loaders/glTF';
import type { Livery } from '../data';
import { applyLivery, type LiveryStyle } from './livery';
import { markSolid } from './postfx';

export type ModelKey = 'taka' | 'aegis' | 'hydra' | 'interceptor' | 'marksman' | 'heavy' | 'drone' | 'turret' | 'boss' | 'convoy' | 'station' | 'vigie' | 'stationBay' | 'pickup' | 'carrier' | 'rock_fallback' | `rock_${number}`;

/** Clés qui réutilisent le fichier d'un autre modèle, chargé à part pour porter leur propre livrée. */
const FILE_ALIAS: Partial<Record<string, string>> = {};


export class ModelLibrary {
  private readonly containers = new Map<string, AssetContainer | null>();
  private readonly fallbackMaterials = new Map<string, StandardMaterial>();
  rockVariants = 0;

  constructor(private readonly scene: Scene) {}

  /** Précharge les modèles ; un échec de chargement est journalisé et remplacé par le repli. */
  async preload(keys: ModelKey[], onProgress?: (done: number, total: number) => void): Promise<string[]> {
    const missing: string[] = [];
    let done = 0;
    await Promise.all(keys.map(async (key) => {
      try {
        const container = await LoadAssetContainerAsync(assetUrl(`models/${FILE_ALIAS[key] ?? key}.glb`), this.scene, { pluginExtension: '.glb' });
        // Lumières du décor (4) et des explosions (4) : voir SceneLights et Vfx.
        for (const material of container.materials) (material as { maxSimultaneousLights?: number }).maxSimultaneousLights = 8;
        this.containers.set(key, container);
      } catch {
        this.containers.set(key, null);
        missing.push(key);
      }
      onProgress?.(++done, keys.length);
    }));
    return missing;
  }

  /** Charge les variantes de rochers disponibles (rock_0, rock_1, …). */
  async preloadRocks(max = 8): Promise<number> {
    const keys = Array.from({ length: max }, (_, i) => `rock_${i}` as ModelKey);
    const missing = new Set(await this.preload(keys));
    this.rockVariants = keys.filter((k) => !missing.has(k)).length;
    return this.rockVariants;
  }

  /** Applique la livrée de faction à tous les matériaux PBR d'un modèle (partagés par ses instances). */
  livery(key: ModelKey, livery: Livery, style: LiveryStyle, environment: number): void {
    const container = this.containers.get(key);
    if (!container) return;
    for (const material of container.materials) applyLivery(material, livery, style, environment);
  }

  /** Matériau mat uniforme des rochers (lisibles, sans reflets chromés). */
  rockMaterial(key: ModelKey, albedo: readonly [number, number, number]): void {
    const container = this.containers.get(key);
    if (!container) return;
    for (const material of container.materials) {
      if (material instanceof PBRMaterial) {
        material.albedoColor = new Color3(albedo[0], albedo[1], albedo[2]);
        material.metallic = 0;
        material.roughness = 0.92;
      }
    }
  }

  has(key: ModelKey): boolean {
    return Boolean(this.containers.get(key));
  }

  /** Instance du modèle sous un nœud racine (que l'appelant positionne). */
  instantiate(key: ModelKey, name: string): TransformNode {
    const container = this.containers.get(key);
    const root = new TransformNode(name, this.scene);
    if (container) {
      const entries = container.instantiateModelsToScene((n) => `${name}-${n}`, false, { doNotInstantiate: false });
      for (const node of entries.rootNodes) node.parent = root;
      for (const mesh of root.getChildMeshes()) {
        mesh.isPickable = false;
        mesh.alwaysSelectAsActiveMesh = false;
        // Objet solide : porte et reçoit les ombres (postfx.ts).
        markSolid(mesh);
      }
    } else {
      this.buildFallback(key, root);
    }
    return root;
  }

  private mat(name: string, color: [number, number, number], emissive = 0): StandardMaterial {
    let m = this.fallbackMaterials.get(name);
    if (!m) {
      m = new StandardMaterial(`fallback-${name}`, this.scene);
      m.diffuseColor = new Color3(...color);
      m.specularColor = new Color3(0.25, 0.25, 0.3);
      m.emissiveColor = new Color3(...color).scale(emissive);
      this.fallbackMaterials.set(name, m);
    }
    return m;
  }

  /** Silhouettes procédurales distinctes par rôle (lisibles même sans modèles). */
  private buildFallback(key: ModelKey, root: TransformNode): void {
    const s = this.scene;
    const add = (mesh: Mesh, material: StandardMaterial) => {
      mesh.parent = root;
      mesh.material = material;
      mesh.isPickable = false;
    };
    const ship = (length: number, span: number, color: [number, number, number], accent: [number, number, number]) => {
      const hull = this.mat(`${key}-hull`, color, 0.08);
      const glow = this.mat(`${key}-accent`, accent, 0.9);
      const body = MeshBuilder.CreateCylinder(`${key}-body`, { height: length, diameterTop: length * 0.04, diameterBottom: length * 0.16, tessellation: 8 }, s);
      body.rotation.x = -Math.PI / 2;
      add(body, hull);
      const wings = MeshBuilder.CreateBox(`${key}-wings`, { width: span, height: length * 0.03, depth: length * 0.32 }, s);
      wings.position.z = length * 0.12;
      add(wings, hull);
      const fin = MeshBuilder.CreateBox(`${key}-fin`, { width: length * 0.02, height: length * 0.14, depth: length * 0.22 }, s);
      fin.position.set(0, length * 0.08, length * 0.3);
      add(fin, hull);
      const cockpit = MeshBuilder.CreateSphere(`${key}-cockpit`, { diameter: length * 0.1, segments: 8 }, s);
      cockpit.position.set(0, length * 0.05, -length * 0.1);
      add(cockpit, glow);
    };
    switch (key) {
      case 'taka': ship(15, 11, [0.55, 0.62, 0.7], [0.1, 0.8, 1]); break;
      case 'aegis': ship(15, 13, [0.45, 0.45, 0.42], [1, 0.45, 0.1]); break;
      case 'hydra': ship(15, 10, [0.35, 0.45, 0.4], [0.3, 1, 0.6]); break;
      case 'interceptor': ship(11.7, 9, [0.55, 0.08, 0.06], [1, 0.2, 0.1]); break;
      case 'drone': ship(11.7, 9, [0.6, 0.25, 0.05], [1, 0.5, 0.08]); break;
      case 'marksman': ship(15, 7, [0.6, 0.5, 0.1], [1, 0.75, 0.15]); break;
      case 'heavy': ship(20.25, 18, [0.5, 0.25, 0.05], [1, 0.5, 0.05]); break;
      case 'boss': ship(37.6, 30, [0.35, 0.05, 0.3], [0.9, 0.1, 1]); break;
      case 'turret': {
        // Tourelle de forteresse (pas de modèle) : boule blindée, collerette, canons jumeaux vers −Z, œil lumineux.
        const armor = this.mat('turret-armor', [0.32, 0.16, 0.14], 0.03);
        armor.specularColor = new Color3(0.45, 0.4, 0.4);
        const dark = this.mat('turret-dark', [0.07, 0.06, 0.06]);
        const ball = MeshBuilder.CreateSphere('turret-ball', { diameter: 8, segments: 16 }, s);
        add(ball, armor);
        const collar = MeshBuilder.CreateTorus('turret-collar', { diameter: 8.2, thickness: 1.1, tessellation: 32 }, s);
        add(collar, dark);
        const mantlet = MeshBuilder.CreateBox('turret-mantlet', { width: 6, height: 2.6, depth: 2 }, s);
        mantlet.position.z = -3.4;
        add(mantlet, dark);
        for (const x of [-1.6, 1.6]) {
          const barrel = MeshBuilder.CreateCylinder(`turret-barrel-${x}`, { height: 6.5, diameter: 0.8, tessellation: 10 }, s);
          barrel.rotation.x = Math.PI / 2;
          barrel.position.set(x, 0, -6.6);
          add(barrel, dark);
          const muzzle = MeshBuilder.CreateCylinder(`turret-muzzle-${x}`, { height: 0.6, diameter: 1.1, tessellation: 10 }, s);
          muzzle.rotation.x = Math.PI / 2;
          muzzle.position.set(x, 0, -9.6);
          add(muzzle, armor);
        }
        const eye = MeshBuilder.CreateSphere('turret-eye', { diameter: 1.3, segments: 8 }, s);
        eye.position.set(0, 1.9, -3.3);
        add(eye, this.mat('turret-eye', [1, 0.2, 0.05], 1));
        break;
      }
      case 'convoy': {
        // Cargo du convoi (pas de modèle) : poutre centrale, conteneurs colorés, passerelle, bloc moteur, nez vers −Z.
        const hull = this.mat('convoy-hull', [0.3, 0.31, 0.33], 0.02);
        const dark = this.mat('convoy-dark', [0.12, 0.13, 0.15]);
        const glow = this.mat('convoy-glow', [0.3, 1, 0.6], 1);
        const spine = MeshBuilder.CreateBox('convoy-spine', { width: 8, height: 8, depth: 96 }, s);
        add(spine, dark);
        const palette: [number, number, number][] = [[0.42, 0.18, 0.06], [0.1, 0.2, 0.32], [0.3, 0.29, 0.27], [0.14, 0.24, 0.14]];
        for (let i = 0; i < 6; i++) {
          for (const [x, y] of [[-7, 5], [7, 5], [-7, -5], [7, -5]] as const) {
            const box = MeshBuilder.CreateBox(`convoy-c${i}`, { width: 6.4, height: 7.4, depth: 12 }, s);
            box.position.set(x, y, -34 + i * 13.5);
            add(box, this.mat(`convoy-cargo-${(i + x + y + 40) % 4}`, palette[(i + (x > 0 ? 1 : 0) + (y > 0 ? 2 : 0)) % 4]!, 0.02));
          }
        }
        const bridge = MeshBuilder.CreateBox('convoy-bridge', { width: 14, height: 10, depth: 14 }, s);
        bridge.position.set(0, 3, -48);
        add(bridge, hull);
        const visor = MeshBuilder.CreateBox('convoy-visor', { width: 12, height: 1.4, depth: 0.4 }, s);
        visor.position.set(0, 5, -55.2);
        add(visor, glow);
        const engine = MeshBuilder.CreateBox('convoy-engine', { width: 20, height: 14, depth: 14 }, s);
        engine.position.set(0, 0, 48);
        add(engine, hull);
        for (const x of [-6, 6]) {
          const nozzle = MeshBuilder.CreateCylinder(`convoy-nozzle-${x}`, { height: 4, diameterTop: 5, diameterBottom: 7, tessellation: 16 }, s);
          nozzle.rotation.x = Math.PI / 2;
          nozzle.position.set(x, 0, 57);
          add(nozzle, dark);
        }
        for (const z of [-30, 0, 30]) {
          const light = MeshBuilder.CreateBox(`convoy-light-${z}`, { width: 9, height: 0.5, depth: 0.5 }, s);
          light.position.set(0, 9.2, z);
          add(light, glow);
        }
        break;
      }
      case 'pickup': {
        const core = MeshBuilder.CreatePolyhedron('pickup', { type: 1, size: 0.9 }, s);
        add(core, this.mat('pickup', [1, 0.5, 0.05], 1));
        break;
      }
      case 'station': {
        const ring = MeshBuilder.CreateTorus('station-ring', { diameter: 1200, thickness: 90, tessellation: 48 }, s);
        add(ring, this.mat('station', [0.35, 0.38, 0.45], 0.05));
        const hub = MeshBuilder.CreateCylinder('station-hub', { height: 1500, diameter: 160, tessellation: 16 }, s);
        hub.rotation.x = Math.PI / 2;
        add(hub, this.mat('station', [0.35, 0.38, 0.45], 0.05));
        break;
      }
      default: {
        // Rocher unitaire : sphère facettée de diamètre 1 (mise à l'échelle des demi-axes par l'appelant).
        const rock = MeshBuilder.CreateIcoSphere(`${key}`, { radius: 0.5, subdivisions: 2, flat: true }, s);
        add(rock, this.mat('rock', [0.32, 0.3, 0.29], 0.02));
      }
    }
  }
}
